import { Reducer, useCallback, useEffect, useMemo, useReducer } from 'react'

import { useWindowKeyPress } from './useWindowKeyPress'

export interface UseKeyboardNavigationOptions {
  listLength: number
  onSelection: (selectedIndex: number) => void
}

export interface UseKeyboardNavigation {
  selectedIndex: number
  onMouseOver: (elementIndex: number) => () => void
}

export interface KeyboardNavigationState {
  selectedIndex: number
}

export type KeyboardNavigationAction =
  | { type: 'arrowUp' }
  | { type: 'arrowDown' }
  | { type: 'mouseEnter'; index: number }

export type KeyboardNavigationReducer = Reducer<KeyboardNavigationState, KeyboardNavigationAction>

const initialState: KeyboardNavigationState = { selectedIndex: 0 }

const keyboardNavigationReducer =
  (listLength: number): KeyboardNavigationReducer =>
  (state, action) => {
    switch (action.type) {
      case 'arrowUp':
        return {
          selectedIndex: state.selectedIndex > 0 ? state.selectedIndex - 1 : listLength - 1,
        }
      case 'arrowDown':
        return {
          selectedIndex: state.selectedIndex < listLength - 1 ? state.selectedIndex + 1 : 0,
        }
      case 'mouseEnter':
        return {
          selectedIndex: action.index,
        }
      default:
        return { ...state }
    }
  }

export const useKeyboardNavigation = ({
  listLength,
  onSelection,
}: UseKeyboardNavigationOptions): UseKeyboardNavigation => {
  const arrowUpPressed = useWindowKeyPress({ targetKey: 'ArrowUp' })
  const arrowDownPressed = useWindowKeyPress({ targetKey: 'ArrowDown' })
  const enterPressed = useWindowKeyPress({ targetKey: 'Enter' })

  const reducer = useMemo(() => keyboardNavigationReducer(listLength), [listLength])

  const [{ selectedIndex }, dispatch] = useReducer(reducer, initialState)

  const onMouseOver = useCallback(
    (elementIndex: number) => () => dispatch({ type: 'mouseEnter', index: elementIndex }),
    [],
  )

  useEffect(() => {
    if (arrowUpPressed) {
      dispatch({ type: 'arrowUp' })
    }
    if (arrowDownPressed) {
      dispatch({ type: 'arrowDown' })
    }
  }, [arrowDownPressed, arrowUpPressed])

  useEffect(() => {
    if (!enterPressed) {
      return
    }
    onSelection(selectedIndex)
  }, [enterPressed, selectedIndex, onSelection])

  return { selectedIndex, onMouseOver }
}
